import * as Constants from 'client/util/Constants';
import { getCloudDirectoryDetailsOrCreateUrl } from 'client/components/GettingStartedPanel/GettingStartedPanelUtils';

import EventHelper from 'client/GrowthEngineering/EventHelper';
import ItemType from 'client/util/Constants/ItemType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';

// Instruction Text Components.
// Tech debt until a markdown solution created for including HTML in text strings
import InstructionTextConfigureAppleMDM from './InstructionTextConfigureAppleMDM.vue';
import InstructionTextDeviceGroup from './InstructionTextDeviceGroup.vue';
import InstructionTextMdmVpp from './InstructionTextMdmVpp.vue';
import InstructionTextUserGroups from './InstructionTextUserGroups.vue';

const {
  DeviceTabs,
  DirectoryActionConsts,
  DirectoryNameConsts,
  DirectoryTypeConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  ItemTypeRoutes,
} = Constants;
const { linux, mac, windows } = Constants.default;

const { ConfigurationItemType, ConfigurationGroupItemType, DeviceItemType } = ItemType;
const configurationItem = new ConfigurationItemType();
const configurationGroupItem = new ConfigurationGroupItemType();
const deviceItem = new DeviceItemType();

const bambooHR = DirectoryNameConsts[DirectoryTypeConsts.bambooHR];
const gsuite = DirectoryNameConsts[DirectoryTypeConsts.gSuite];
const o365 = DirectoryNameConsts[DirectoryTypeConsts.office365];
const okta = DirectoryNameConsts[DirectoryTypeConsts.okta];
const workday = DirectoryNameConsts[DirectoryTypeConsts.workday];
const gettingStartedPanelSettings = {};

const {
  activeDirectory,
  authNPolicies,
  browserPatchManagement,
  cloudDirectory,
  cloudInsights,
  command,
  commandResult,
  configuration,
  configurationGroup,
  device,
  deviceGroup,
  hrDirectory,
  ipList,
  ldap,
  sso,
  mdmSettings,
  mdmVpp,
  osPatchManagement,
  pki,
  radiusServer,
  softwareApps,
  systemUser,
  vppApps,
  userGroup,
} = ItemTypeConsts;

gettingStartedPanelSettings[cloudInsights] = {
  clickActionDescription: 'add',
  headerText: 'See the power of Cloud Insights and view event data from your AWS Accounts',
  learnMoreUrl: KbArticleUrls.gettingStartedSystems,
  mainText: [
    {
      text: 'Get started with Cloud Insights by adding one of your AWS Accounts.',
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/Getting-Started-Cloud-Insights',
        text: 'Learn more',
      },
    },
  ],
};

gettingStartedPanelSettings[device] = {
  clickActionDescription: 'add',
  headerText: `Add your first ${deviceItem.label}`,
  learnMoreUrl: KbArticleUrls.gettingStartedSystems,
  mainText: [
    {
      text: `Manage user access, adjust settings, and apply security ${configurationItem.labelPlural}.`,
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/getting-started-systems1-2019-08-21-10-36-47',
        text: 'Learn how.',
      },
    },
  ],
  tooltipText: `Mac, Windows, or Linux ${deviceItem.labelPlural} are bound to JumpCloud by installing JumpCloud's system agent. Once installed and bound, you can remotely and securely manage these ${deviceItem.labelPlural} and their user accounts, set ${configurationItem.labelPlural}, execute commands, enable MFA, and more.`,
  waysToAddCards: [
    {
      title: 'Mac',
      icon: 'img/icons/macos-logo.svg',
      redirectUrl: `${deviceItem.route}/new/${DeviceTabs.macos}`,
      guidedSetup: {
        text: 'Guided Setup for Mac',
        device: mac,
      },
    },
    {
      title: 'Windows',
      icon: 'img/icons/windows-logo.svg',
      guidedSetup: {
        text: 'Guided Setup for Windows',
        device: windows,
      },
      redirectUrl: `${deviceItem.route}/new/${DeviceTabs.windows}`,
    },
    {
      title: 'Linux',
      icon: 'img/icons/linux-logo.svg',
      guidedSetup: {
        text: 'Guided Setup for Linux',
        device: linux,
      },
      redirectUrl: `${deviceItem.route}/new/${DeviceTabs.linux}`,
    },
  ],
};

/**
/* Converted this to a method to be able to pass a Feature
/* Flag as a param. This is because we don't have access to the FF in this file.
/* (The store.getters.. approach won't work here either)
 */
gettingStartedPanelSettings[systemUser] = featureFlags => ({
  clickActionDescription: 'add',
  headerText: 'Add your first user',
  learnMoreUrl: KbArticleUrls.gettingStartedUsers,
  mainText: [
    {
      text: 'Grant user identities access to workstations, servers, networks, apps, and files. ',
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/getting-started-users1-2019-08-21-10-36-47',
        text: 'Learn how.',
      },
    },
  ],
  tooltipText: 'User identities are at the core of JumpCloud. As a core directory, JumpCloud provides centralized, authoritative versions of each employee\'s identities so they can use a single set of credentials across all their resources.',
  waysToAddCards: [
    {
      title: 'Manual entry',
      icon: 'img/growth/icon-manual-user-entry-tile.svg',
      id: 'manual-entry',
      expandedCopy: 'Recommended if you don’t have a directory service and no more than 20 users.',
      redirectUrl: `${ItemTypeRoutes[ItemTypeConsts.systemUser]}/add`,
      learnMoreLinks: [
        {
          href: 'https://jumpcloud.com/course/creating-users/#/lessons/wFE_3gdarX6FfW84qRupXmaH5E_xWAIm',
          text: 'Watch Manual User Creation',
        },
        {
          href: 'https://support.jumpcloud.com/support/s/article/getting-started-users1-2019-08-21-10-36-47',
          text: 'Read Getting started: Users',
        },
      ],
    },
    {
      title: 'Import via CSV',
      icon: 'img/growth/icon-import-from-csv-tile.svg',
      id: 'import-via-CSV',
      expandedCopy: 'Recommended if you have an existing directory service and 20+ users.',
      learnMoreLinks: [
        {
          href: 'https://jumpcloud.com/course/creating-users/#/lessons/dz5_ahayGyZqMJbzp1WpUqKHKiT8gSq6',
          text: 'Watch CSV User Import',
        },
        {
          href: 'https://support.jumpcloud.com/support/s/article/Import-Users-from-CSV-in-the-Admin-Portal',
          text: 'Read Import Users from a CSV File',
        },
      ],
    },
    {
      title: `Import via ${gsuite}`,
      icon: 'img/growth/icon-import-google-workspace-tile.svg',
      id: 'import-via-google',
      expandedCopy: `Recommended if you use ${gsuite}.`,
      async redirectUrl() {
        return getCloudDirectoryDetailsOrCreateUrl(DirectoryTypeConsts.gSuite);
      },
      learnMoreLinks: [
        {
          href: 'https://support.jumpcloud.com/support/s/article/G-Suite-Integration-Vs-G-Suite-SAML-Connector',
          text: 'Application vs directory integration',
        },
        {
          href: 'https://jumpcloud.com/course/creating-users/#/lessons/jB1lRC2k40PKtKuR4BzZ6Mj-S6UtAyso',
          text: `Watch ${gsuite} Sync`,
        },
        {
          href: 'https://support.jumpcloud.com/support/s/article/G-Suite-Integration-Scenarios',
          text: `Read ${gsuite} Integration Scenarios`,
        },
      ],
    },
    {
      title: `Import via ${o365}`,
      icon: 'img/growth/icon-import-m365-tile.svg',
      id: 'import-via-m365',
      expandedCopy: `Recommended if you use ${o365}.`,
      async redirectUrl() {
        return getCloudDirectoryDetailsOrCreateUrl(DirectoryTypeConsts.office365);
      },
      learnMoreLinks: [
        {
          href: 'https://support.jumpcloud.com/support/s/article/Office365-Integration-vs-Saml-Connector',
          text: 'Application vs directory integration',
        },
        {
          href: 'https://jumpcloud.com/course/creating-users/#/lessons/w8oiYQL8gt4MDPD2sHRU41rBzgM6CQHl',
          text: `Watch ${o365} Sync`,
        },
        {
          href: 'https://support.jumpcloud.com/support/s/article/office-365-user-import-provisioning-and-sync1',
          text: `Read ${o365} User Import, Provisioning, and Sync`,
        },
      ],
    },
    {
      title: 'Import via Active Directory',
      icon: 'img/growth/icon-active-directory-tile.svg',
      id: 'import-via-ad',
      expandedCopy: 'Recommended if you are extending AD and have no plans to migrate off of AD. If you’re migrating off of AD, we recommend using one of the alternative options above.',
      redirectUrl: `${ItemTypeRoutes[ItemTypeConsts.activeDirectory]}/${DirectoryTypeConsts.activeDirectory}/${DirectoryActionConsts.create}`,
      learnMoreLinks: [
        {
          href: 'https://support.jumpcloud.com/support/s/article/integrating-ad-with-jumpcloud',
          text: 'Integrate Active Directory with JumpCloud',
        },
        {
          href: 'https://support.jumpcloud.com/support/s/article/installing-the-ad-import-agent1',
          text: 'Configure & Install Active Directory Import Agent',
        },
        {
          href: 'https://support.jumpcloud.com/support/s/article/installing-the-ad-sync-agent',
          text: 'Configure & Install Active Directory Sync Agent',
        },
      ],
    },
    {
      title: `Import via ${bambooHR}`,
      icon: 'img/growth/icon-import-bamboohr-tile.svg',
      id: 'import-via-bamboo',
      expandedCopy: `Recommended if you use ${bambooHR} as the source of truth for employee information.`,
      // Using the Feature Flag to hide this specific card.
      hide: !featureFlags.bambooRealTimeImport,
      redirectUrl: 'https://support.jumpcloud.com/support/s/article/Integrating-BambooHR-with-JumpCloud',
      external: true,
      learnMoreLinks: [
        {
          href: 'https://support.jumpcloud.com/support/s/article/Integrating-BambooHR-with-JumpCloud',
          text: `Integrate ${bambooHR} with JumpCloud`,
        },
      ],
    },
    {
      title: `Import via ${okta}`,
      icon: 'img/growth/icon-import-okta-tile.svg',
      id: 'import-via-okta',
      expandedCopy: `Recommended if you use ${okta} for user identity management.`,
      redirectUrl: KbArticleUrls.oktaIntegration,
      external: true,
      learnMoreLinks: [
        {
          href: KbArticleUrls.oktaIntegration,
          text: `Integrate ${okta} with JumpCloud`,
        },
      ],
    },
  ],
});

gettingStartedPanelSettings[configuration] = {
  clickActionDescription: `create a ${configurationItem.label}`,
  headerText: `Apply ${configurationItem.label} to ${deviceItem.label}`,
  learnMoreUrl: KbArticleUrls.gettingStartedPolicies,
  mainText: [
    {
      text: `Protect user devices by applying ${configurationItem.labelPlural} to device groups. `,
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/getting-started-policies-2019-08-21-10-36-47',
        text: 'Learn how.',
      },
    },
  ],
  tooltipText: `Set up security ${configurationItem.labelPlural} to control all (or some) of the devices in your organization. Hint: Setting up a Lock Screen ${configurationItem.label} is a great place to start.`,
};

gettingStartedPanelSettings[osPatchManagement] = gettingStartedPanelSettings[configuration];

gettingStartedPanelSettings[configurationGroup] = {
  clickActionDescription: `create a ${configurationGroupItem.labelPlural}`,
  headerText: `Apply ${configurationGroupItem.labelPlural} to devices`,
  learnMoreUrl: KbArticleUrls.gettingStartedPolicies,
  mainText: [
    {
      text: `Protect user devices by creating a ${configurationGroupItem.label} to manage a group of devices.`,
      link: {
        href: 'https://jumpcloud-support.force.com/support/s/article/Basic-Policy-Procedures',
        text: 'Learn More.',
      },
    },
    {
      text: `Click + to create a ${configurationGroupItem.title}.`,
    },
  ],
  tooltipText: `Select which ${configurationItem.labelPlural} to assign to a ${configurationGroupItem.title} to manage device security`,
};

gettingStartedPanelSettings[deviceGroup] = {
  clickActionDescription: 'create a group',
  headerText: 'Add a group of Devices',
  learnMoreUrl: KbArticleUrls.gettingStartedGroups,
  mainText: [
    {
      component: InstructionTextDeviceGroup,
      componentData: configurationItem.labelPlural,
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/getting-started-groups',
        text: 'Learn how.',
      },
    },
    {
      text: 'Click the green + button to create a new Device Group.',
    },
  ],
  tooltipText: `Select a Group of Devices to perform group-based assignments to resources. Device Groups pool together your devices for ${configurationItem.label} enforcement and user account provisioning at scale.`,
};

gettingStartedPanelSettings[userGroup] = {
  clickActionDescription: 'create a group',
  headerText: 'Add a group of Users',
  learnMoreUrl: KbArticleUrls.gettingStartedGroups,
  mainText: [
    {
      component: InstructionTextUserGroups,
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/getting-started-groups',
        text: 'Learn how.',
      },
    },
    {
      text: 'Click the green + button to create a new User Group.',
    },
  ],
  tooltipText: 'Select a Group of Users to perform group-based assignments to resources. User Groups provide your users access to resources.',
};

gettingStartedPanelSettings[sso] = featureFlags => ({
  clickActionDescription: 'configure',
  headerText: featureFlags.navBarSsoExperiment ? 'Add your first application' : `Add your first ${ItemTypeDisplayNames[sso]} application`,
  learnMoreUrl: KbArticleUrls.gettingStartedApplications,
  mainText: [
    {
      text: 'Control user access to web apps via SAML 2.0. ',
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/getting-started-applications-saml-sso2',
        text: 'Learn how.',
      },
    },
    {
      text: 'Click the green + button to view our full list of pre-built SSO connectors.',
    },
  ],
  proTip: {
    text: 'If you have on-premises or legacy apps that use LDAP, check out our ',
    link: {
      href: KbArticleUrls.LdapKB,
      text: 'LDAP directory.',
    },
  },
  tooltipText: "Select and configure an application you would like JumpCloud to authenticate via SAML 2.0 with. For on-premises or services that may not support SAML 2.0, JumpCloud's LDAP authentication may be used.",
});

const activeDirectoryText = 'Authorize JumpCloud\'s sync connection with Active Directory to import users from AD and leverage continual bi-directional account synchronization. To learn more, read ';

gettingStartedPanelSettings[activeDirectory] = {
  clickActionDescription: 'configure',
  headerText: 'Configure Active Directory',
  learnMoreUrl: KbArticleUrls.gettingStartedActiveDirectory,
  mainText: [
    {
      text: activeDirectoryText,
      link: {
        href: KbArticleUrls.gettingStartedActiveDirectory,
        text: 'Getting Started: Active Directory and JumpCloud.',
      },
    },
  ],
  tooltipText: activeDirectoryText,
  tooltipExtraLinks: [
    {
      href: KbArticleUrls.adiTroubleshooting,
      text: 'View Troubleshooting ADI Guide',
    },
  ],
};

gettingStartedPanelSettings[authNPolicies] = {
  headerText: 'Control how users can access company resources',
  learnMoreUrl: 'https://support.jumpcloud.com/support/s/article/Getting-Started-Conditional-Access-Policies',
  mainText: [
    {
      text: 'Conditional Policies will enhance your security posture by determining who can access applications or the User Portal',
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/Getting-Started-Conditional-Access-Policies',
        text: 'Learn more.',
      },
    },
  ],
};

const cloudDirectoryText = `Authorize JumpCloud's sync connections with ${gsuite} and ${o365} to import users and leverage continual bi-directional account synchronization.`;

gettingStartedPanelSettings[cloudDirectory] = {
  clickActionDescription: 'configure',
  headerText: 'Configure Cloud Directories',
  learnMoreUrl: KbArticleUrls.gettingStartedDirectories,
  mainText: [
    {
      text: cloudDirectoryText,
      link: {
        href: KbArticleUrls.gettingStartedDirectories,
        text: 'Learn more.',
      },
    },
  ],
  tooltipText: cloudDirectoryText,
};

/**
/* Converted this to a method to be able to pass a Feature
/* Flag as a param. This is because we don't have access to the FF in this file.
/* (The store.getters.. approach won't work here either)
 */
gettingStartedPanelSettings[hrDirectory] = (featureFlags) => {
  const hrDirectoryText = featureFlags.bambooRealTimeImport
    ? `Authorize JumpCloud's connection with ${workday} or ${bambooHR} and import user accounts.`
    : `Authorize JumpCloud's connection with ${workday} and import user accounts.`;
  return {
    clickActionDescription: 'configure',
    headerText: featureFlags.bambooRealTimeImport ? `Configure ${workday} or ${bambooHR} Real-time Sync` : `Configure ${workday}`,
    learnMoreUrl: KbArticleUrls.gettingStartedWorkday,
    mainText: [
      {
        text: hrDirectoryText,
        link: {
          href: KbArticleUrls.gettingStartedWorkday,
          text: 'Learn more.',
        },
      },
    ],
    tooltipText: hrDirectoryText,
  };
};

gettingStartedPanelSettings[ipList] = {
  headerText: 'Configure IP Lists to add to Conditional Access Policies',
  learnMoreUrl: KbArticleUrls.ipList,
  mainText: [
    {
      text: 'IP Lists can be used to allow or deny user authentication based on the network a user is on.',
      link: {
        href: KbArticleUrls.ipList,
        text: 'Learn more.',
      },
    },
  ],
};

gettingStartedPanelSettings[ldap] = {
  clickActionDescription: 'configure',
  headerText: 'Configure Cloud LDAP',
  learnMoreUrl: KbArticleUrls.gettingStartedDirectories,
  mainText: [
    {
      text: 'Enable access to on-premises, legacy, and open source apps without managing any on-prem LDAP infrastructure. ',
      link: {
        href: 'https://support.jumpcloud.com/support/s/topic/0TO1M000000EUx3WAG/ldapasaservice',
        text: 'Learn how.',
      },
    },
  ],
  proTip: {
    text: 'To enable LDAP, you need to have at least one user that’s enabled as LDAP Bind DN.',
    link: {
      href: KbArticleUrls.LdapKB,
      segmentEvent: EventHelper.triggerGettingStartedLdapProtipLearnMoreClicked,
      text: 'Learn more.',
    },
  },
  tooltipText: 'JumpCloud integrates with a variety of popular services to synchronize user accounts. These integrations enable JumpCloud to act as the authoritative directory to ensure a single set of credentials across all services. This includes our own cloud-based LDAP services which you can configure here to attach to on-prem or legacy resources.',
};

const commandsResultsTextTooltip = 'Commands allow you to execute scripts across fleets of machines through JumpCloud\'s system agent. You can deploy files, schedule maintenance activity, or install software on endpoints in PowerShell, bash, shell, and more. You can run commands across any number of devices in parallel and retrieve command results (including stdout, stderr, and exit codes).';

gettingStartedPanelSettings[command] = {
  clickActionDescription: 'create',
  headerText: 'Create and execute your first command',
  learnMoreUrl: KbArticleUrls.gettingStartedCommands,
  mainText: [
    {
      text: 'Create and run scripts for device checks, compliance reports, and software installations.',
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/getting-started-commands-2019-08-21-10-36-47',
        text: 'Learn how.',
      },
    },
  ],
  proTip: {
    text: 'Commands can be scheduled, triggered by events, or run ad hoc.',
  },
  tooltipText: commandsResultsTextTooltip,
};

gettingStartedPanelSettings[commandResult] = {
  clickActionDescription: 'create',
  headerText: 'Create and execute your first command',
  learnMoreUrl: KbArticleUrls.gettingStartedCommands,
  mainText: [
    {
      text: 'Select a command in the Commands tab and run it to see the results.',
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/getting-started-commands-2019-08-21-10-36-47',
        text: 'Learn how.',
      },
    },
  ],
  tooltipText: commandsResultsTextTooltip,
};

gettingStartedPanelSettings[radiusServer] = {
  clickActionDescription: 'configure',
  headerText: 'Secure networks with Cloud RADIUS',
  learnMoreUrl: KbArticleUrls.gettingStartedRadius,
  mainText: [
    {
      text: 'Add a RADIUS server to extend your users’ JumpCloud credentials to your WiFi, VPNs, and other RADIUS-based resources. ',
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/getting-started-radius1',
        text: 'Learn how.',
      },
    },
  ],
  tooltipText: 'JumpCloud\'s cloud-based RADIUS service extends your users\' JumpCloud credentials to your WiFi and other resources supporting the RADIUS protocol. Each RADIUS server can be bound to specific user groups to control which users can access those resources.',
};

gettingStartedPanelSettings[softwareApps] = {
  clickActionDescription: 'create',
  headerText: 'Add your first managed software',
  learnMoreUrl: KbArticleUrls.gettingStartedSoftwareApps,
  mainText: [
    {
      text: 'Install software on user devices. Compatible with Windows.',
      link: {
        href: KbArticleUrls.gettingStartedSoftwareApps,
        text: 'Learn more.',
      },
    }],
  tooltipText: '',
};

gettingStartedPanelSettings[mdmSettings] = {
  clickActionDescription: 'configure',
  headerText: 'Configure Apple MDM',
  learnMoreUrl: KbArticleUrls.gettingStartedMdm,
  mainText: [
    {
      text: 'Configure MDM to unlock additional capabilities only available via Apple’s MDM protocol, such as remote wipe, zero-touch deployment, ',
      link: {
        href: 'https://support.jumpcloud.com/support/s/article/Getting-Started-MDM',
        text: 'and more.',
      },
    },
    {
      component: InstructionTextConfigureAppleMDM,
      componentData: configurationItem.titlePlural,
    },
  ],
  tooltipText: '',
};

gettingStartedPanelSettings[mdmVpp] = {
  clickActionDescription: 'add token',
  learnMoreUrl: KbArticleUrls.mdmVpp,
  headerText: 'Configure VPP',
  mainText: [
    {
      component: InstructionTextMdmVpp,
      link: {
        href: KbArticleUrls.mdmVpp,
        text: 'Learn how.',
      },
    },
  ],
  tooltipText: '',
};

gettingStartedPanelSettings[vppApps] = {
  headerText: 'Configure VPP or Add Apps',
  mainText: [
    {
      text: 'You can add apps from the Apple Store by configuring Apple’s Volume Purchase (VPP) for your organization. You can also add preconfigured custom app packages that are available outside of the App Store.',
    }],
  tooltipText: '',
  waysToAddCards: [
    {
      id: 'addToken',
      title: 'Configure VPP',
      icon: 'img/icons/macos-logo.svg',
      mainText: "You'll need an account with [Apple Business Manager](https://business.apple.com/) or [Apple School Manager](https://school.apple.com/) to purchase VPP licenses. Click **Add Token** to download a location token to set up VPP. Learn more: [Setting Up Apple's VPP](https://jumpcloud-support.force.com/support/s/article/Setting-Up-Apple-VPP).",
      link: {
        redirectUrl: `${ItemTypeRoutes.mdmSettings}`,
        text: 'Add Token',
      },
    },
    {
      id: 'addCustomApp',
      title: 'Add an App',
      icon: 'img/icons/customapps-logo.svg',
      mainText: 'Preconfigured custom app packages are developed and signed by you or a third-party to meet a specific business need for your organization. These apps are hosted in secure, publicly-accessible repositories. Learn more: [Software Management: MacOS](https://jumpcloud-support.force.com/support/s/article/Software-Management-Mac-OS).',
      link: {
        redirectUrl: `${ItemTypeRoutes[ItemTypeConsts.softwareApps]}/apple/new`,
        text: 'Add',
      },
    },
  ],
};

gettingStartedPanelSettings[osPatchManagement] = {
  headerText: 'Configure OS Patch Policies',
  headerIcon: 'premiumDiscover',
  mainText: [
    {
      text: 'JumpCloud’s automated patch management helps you secure your fleet to remotely manage, schedule and install OS updates for your macOS, Windows, and Ubuntu devices.',
      link: {
        href: '',
        text: '',
      },
    },
  ],
};

gettingStartedPanelSettings[browserPatchManagement] = {
  headerText: 'Configure Browser Patch Policies',
  headerIcon: 'premiumDiscover',
  mainText: [
    {
      text: 'JumpCloud’s automated browser patch management helps you secure your fleets\' browsers using cross-platform policies that enforce update, reporting, & employee experience settings on macOS, Windows, and Linux devices.',
      link: {
        href: '',
        text: '',
      },
    },
  ],
};

gettingStartedPanelSettings[pki] = {
  headerText: 'Add Your First Certificate',
  mainText: [
    {
      text: 'Certificate lifecycle management for frictionless certificate based authentication into RADIUS.',
      link: {
        href: '#',
        text: 'Learn more',
      },
    },
  ],
};

export default gettingStartedPanelSettings;
